import { useState, useEffect, useRef } from "react";

const SYMBOLS = [
  { sym: "BTC",  pair: "BTC/USDT",  bSym: "btcusdt"  },
  { sym: "ETH",  pair: "ETH/USDT",  bSym: "ethusdt"  },
  { sym: "BNB",  pair: "BNB/USDT",  bSym: "bnbusdt"  },
  { sym: "SOL",  pair: "SOL/USDT",  bSym: "solusdt"  },
  { sym: "XRP",  pair: "XRP/USDT",  bSym: "xrpusdt"  },
  { sym: "DOGE", pair: "DOGE/USDT", bSym: "dogeusdt" },
  { sym: "ADA",  pair: "ADA/USDT",  bSym: "adausdt"  },
  { sym: "TRX",  pair: "TRX/USDT",  bSym: "trxusdt"  },
  { sym: "AVAX", pair: "AVAX/USDT", bSym: "avaxusdt" },
  { sym: "DOT",  pair: "DOT/USDT",  bSym: "dotusdt"  },
  { sym: "LINK", pair: "LINK/USDT", bSym: "linkusdt" },
  { sym: "POL",  pair: "POL/USDT",  bSym: "polusdt"  },
];

const initPrices = () =>
  SYMBOLS.map(s => ({ ...s, price: 0, pct: "0.00", up: true }));

async function fetchBinance(onUpdate) {
  try {
    const syms = SYMBOLS.map(s => `"${s.bSym.toUpperCase()}"`).join(",");
    const res  = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbols=[${syms}]`);
    const json = await res.json();
    if (!Array.isArray(json)) return;
    json.forEach(d => {
      const sym = SYMBOLS.find(s => s.bSym.toUpperCase() === d.symbol);
      if (!sym) return;
      const price = parseFloat(d.lastPrice) || 0;
      const pct   = parseFloat(d.priceChangePercent) || 0;
      onUpdate(sym.sym, price, pct);
    });
  } catch {}
}

export default function useBinancePrices() {
  const [prices, setPrices] = useState(initPrices);
  // Mutable ref so ticker can read latest without re-render
  const pricesRef   = useRef(initPrices());
  const wsRef       = useRef(null);
  const intervalRef = useRef(null);
  // Batch timer — collect updates for 500ms then flush once
  const pendingRef  = useRef({});
  const flushTimer  = useRef(null);

  const flush = () => {
    const updates = pendingRef.current;
    if (!Object.keys(updates).length) return;
    pendingRef.current = {};
    setPrices(prev => {
      const next = prev.map(p =>
        updates[p.sym]
          ? { ...p, ...updates[p.sym] }
          : p
      );
      pricesRef.current = next;
      return next;
    });
  };

  const queueUpdate = (sym, price, pct) => {
    pendingRef.current[sym] = {
      price,
      pct: (pct >= 0 ? "+" : "") + pct.toFixed(2),
      up: pct >= 0,
    };
    clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, 500);
  };

  useEffect(() => {
    fetchBinance(queueUpdate);

    try {
      const streams = SYMBOLS.map(s => `${s.bSym}@ticker`).join("/");
      const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          const d   = msg.data;
          if (!d || !d.s) return;
          const sym = SYMBOLS.find(s => s.bSym.toUpperCase() === d.s);
          if (!sym) return;
          queueUpdate(sym.sym, parseFloat(d.c) || 0, parseFloat(d.P) || 0);
        } catch {}
      };

      ws.onerror = () => {
        intervalRef.current = setInterval(() => fetchBinance(queueUpdate), 10000);
      };
      ws.onclose = () => {
        if (!intervalRef.current)
          intervalRef.current = setInterval(() => fetchBinance(queueUpdate), 10000);
      };
    } catch {
      intervalRef.current = setInterval(() => fetchBinance(queueUpdate), 10000);
    }

    return () => {
      wsRef.current?.close();
      clearInterval(intervalRef.current);
      clearTimeout(flushTimer.current);
    };
  }, []);

  return prices;
}
